import React, { useEffect, useMemo, useState } from "react";
import { Modal, Input, Radio, Typography, Spin } from "antd";
import "./index.less";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/reducer/store";
import {
    updateAddOrShowFavouritesModel,
    updateAuthorCode,
    updateCommonSuccessMessage,
    updateCommonWarnMessage,
    updateSelectedLocationData,
    updateSelectedFavouriteItem,
    updateFetchedChartData,
    updateSelectedFavouriteId
}
    from "@/redux/actions/climateShiftAction";
import {
    ClimateShiftAddOrShowFavouritesEnum,
    ClimateShiftFavouritesEnum
} from "@/util/cosntants";
import IF from "@/components/IF";
import { isExistsAuthorCode, operateFavorite } from "@/util/http/ClimateShift";
import { debounce } from "lodash";
import { IFavoriteModel } from "@/util/http/ClimateShift/responseType";
import deleteImg from "@/resource/images/png/climateShift/Delete.png";
import leftArrow from "@/resource/images/png/climateShift/left-arrow.png";
import { use } from "echarts";

const { Title } = Typography;
const FavouritesModal = (props: any) => {

    const dispatch = useDispatch();

    const selectedFavouriteId = useSelector((state: RootState) => state.climateShiftReducer.selectedFavouriteId);

    const handleCancel = () => {
        dispatch(updateAddOrShowFavouritesModel(null));
    };

    /**
     * For Add
     * 1. if no author code, then should show input box to input author code
     * 2. if author code is exist, show input box to input favourites name
     *
     * For Show
     * 1. if no author code, then should show input box to input author code
     *    then check if author code is exist, if exist, then show tooltip for code match, though show code new tootip
     *    then click process button, should get all favourites list
     * 2. if author code is exist, then show all favourites list
     *
     * 3. provide a return button to return input page
     */

    // Redux state
    const authorCode = useSelector((state: RootState) => state.climateShiftReducer.authorCode);
    const addOrShowFavouritesModal = useSelector((state: RootState) => state.climateShiftReducer.addOrShowFavouritesModal);
    const favouritesOptions = useSelector((state: RootState) => state.climateShiftReducer.selectedFavouriteItem);

    // Local state
    const [inputAuthorCode, setInputAuthorCode] = React.useState<string>("");
    const [ifExistAuthorCode, setIfExistAuthorCode] = React.useState<boolean | undefined>(undefined);
    const [favouritesName, setFavouritesName] = React.useState<string>("");
    const [allFavoriteList, setAllFavoriteList] = React.useState<IFavoriteModel[]>([]);

    // for show all Favourites
    const publicHandleProcess = async () => {
        if (addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.SHOW && inputAuthorCode) {
            localStorage.setItem("authorCode", inputAuthorCode);
            dispatch(updateAuthorCode(inputAuthorCode));

            // 关键修复：使用await等待数据获取完成
            await publicGetAllFavouritesList(inputAuthorCode);
        }

        // for Add
        if (addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.ADD) {
            if (!authorCode || !favouritesName) {
                // mean should add a new Favourites
                // handle show Favourites, mean should get all Favourites list
                localStorage.setItem("authorCode", inputAuthorCode);
                dispatch(updateAuthorCode(inputAuthorCode));
            }
            else if (authorCode && favouritesName) {
                // mean should add a new Favourites
                try {
                    await operateFavorite({
                        operation: ClimateShiftFavouritesEnum.ADD,
                        authorCode: authorCode,
                        name: favouritesName,
                        options: favouritesOptions,
                    });
                    dispatch(updateAddOrShowFavouritesModel(null));
                    dispatch(updateCommonSuccessMessage("Favourites Saved Successfully!"));
                } catch (error) {
                    dispatch(updateCommonWarnMessage("Save failed. The entered name already exists."));
                }
            }
        }

    };

    const publicHandleReturn = () => {
        // handle return, mean should return to input author code page
        setInputAuthorCode("");
        setIfExistAuthorCode(undefined);
        setFavouritesName("");
        dispatch(updateAuthorCode(""));
        localStorage.removeItem("authorCode");
    };

    // 防抖后的函数，确保只设置当前输入的结果
    const publicHandleAuthorCodeInput = debounce(async (value?: string) => {
        setInputAuthorCode(value || "");
        try {
            const res = await isExistsAuthorCode(value);
            // 只在 value 等于当前输入时才设置，避免异步覆盖
            setIfExistAuthorCode(prev => {
                // value 可能为 undefined/null
                if ((value || "") === (document.activeElement as HTMLInputElement)?.value) {
                    return res.data;
                }
                return prev;
            });
        } catch (e) {
            console.error("Error checking author code existence:", e);
        }
    }, 30);

    const publicHandleInput = (value?: string) => {
        if (addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.ADD) {
            setFavouritesName(value || "");
        }
        else if (addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.SHOW) {
            // handle show Favourites, mean should get all Favourites list
            dispatch(updateAuthorCode(value || ""));
        }
    };

    const [isLoading, setIsLoading] = useState(false);
    const publicGetAllFavouritesList = async (authorCode: string) => {
        setIsLoading(true);
        try {
            const res = await operateFavorite({
                operation: ClimateShiftFavouritesEnum.SEARCH,
                authorCode: authorCode,
            });
            if (res?.data?.length > 0) {
                //console.log("before update allFavoriteList:", allFavoriteList);
                setAllFavoriteList(res.data);
                setSearchQuery("");
                //console.log("after update allFavoriteList:", res.data);
            } else {
                setAllFavoriteList([]);
                console.log("get empty");
            }
        }
        catch (e) {
            console.error("Error getting all Favourites list:", e);
        } finally {
            setIsLoading(false);
        }
    };


    useEffect(() => {
        if (addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.SHOW ||
            addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.ADD) {
            // get author code
            const tmpAuthorCode = localStorage.getItem("authorCode");
            if (tmpAuthorCode) {
                dispatch(updateAuthorCode(tmpAuthorCode));
                // if is show, the should get all favourites list
                if (addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.SHOW) {
                    publicGetAllFavouritesList(tmpAuthorCode);
                }
            }
        }
        // if author code is exist, then set inputAuthorCode
        if (addOrShowFavouritesModal !== ClimateShiftAddOrShowFavouritesEnum.SHOW &&
            addOrShowFavouritesModal !== ClimateShiftAddOrShowFavouritesEnum.ADD) {
            setInputAuthorCode("");
            setIfExistAuthorCode(undefined);
            setFavouritesName("");
        }
    }, [addOrShowFavouritesModal, authorCode]);

    //const [selectedId, setSelectedId] = useState<number | null>(null);
    const handleDelete = async (item) => {
        try {
            await operateFavorite({
                operation: ClimateShiftFavouritesEnum.DELETE,
                authorCode: item?.authorCode,
                id: item?.id,
                name: item?.name
            });

            setAllFavoriteList(prev => prev.filter(fav => fav.id !== item.id));

            setSearchQuery("");
            dispatch(updateCommonSuccessMessage("Favourites deleted successfully!"));
        } catch (error) {
            dispatch(updateCommonWarnMessage("Failed to delete the favourite item."));
        }
    };

    const [searchQuery, setSearchQuery] = useState("");

    const filteredFavoriteList = useMemo(() => {
        if (!searchQuery.trim()) return allFavoriteList;
        const query = searchQuery.toLowerCase().trim();
        return allFavoriteList.filter(item =>
            item.name.toLowerCase().includes(query)
        );
    }, [allFavoriteList, searchQuery]);

    const handleOpen = () => {
        let tmpOptions = {};
        if (selectedFavouriteId === null) {
            dispatch(updateCommonWarnMessage("Please select a favourite first."));
            return;
        }
        const tempSelectedFavorite = allFavoriteList.find(item => item.id === selectedFavouriteId);
        if (!tempSelectedFavorite) {
            dispatch(updateCommonWarnMessage("The selected favourite was not found."));
            return;
        }
        tmpOptions = {
            locationNameOrCoordinate: tempSelectedFavorite?.name,
            selectedLocationItem: tempSelectedFavorite?.options?.selectedLocationItem,
        };

        dispatch(updateSelectedFavouriteItem(tmpOptions));
        dispatch(updateFetchedChartData([]));//Important, reset chart data
        dispatch(updateSelectedLocationData(tempSelectedFavorite?.options?.selectedLocationItem));
        handleCancel();
    };

    return (
        <Modal
            title={
                <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
                    <IF show={!!authorCode && (addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.SHOW ||
                        addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.ADD
                    )}>
                        <div style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                            onClick={() => {
                                publicHandleReturn();
                            }}>
                            <img src={leftArrow} />
                        </div>
                    </IF>
                    <Title level={4} style={{ margin: 0 }}>
                        Favourites
                    </Title>
                </div>
            }
            open={addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.SHOW ||
                addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.ADD
            }
            onCancel={handleCancel}
            width={360}
            className="climate-shift-modal"
            footer={null}
            getContainer={() => document.getElementById("climate-shift-component") as HTMLElement}
        >
            <div className="climate-shift-modal-content">
                <div className="climate-shift-modal-content-enter">

                    {/* ClimateShiftAddOrShowFavouritesEnum为show，并且author有值时，会显示 */}
                    <Spin spinning={isLoading}>
                        <IF show={!!authorCode && addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.SHOW}>
                            {/* then show all Favourites list */}
                            <div className="favourite-search-container">
                                <Input
                                    type="text"
                                    placeholder="Search"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    className="favourite-search-input"
                                />
                            </div>
                            {/* 根据搜索结果筛选后的收藏列表 */}
                            {filteredFavoriteList.length > 0 ? (
                                <Radio.Group
                                    value={selectedFavouriteId}
                                    onChange={(e) => dispatch(updateSelectedFavouriteId(e.target.value))}
                                    style={{ display: "flex", flexDirection: "column" }}
                                >
                                    {filteredFavoriteList.map((item) => (
                                        <div
                                            key={item.id}
                                            className="favourite-list"
                                        >
                                            <Radio value={item.id}>{item.name}</Radio>
                                            <img
                                                src={deleteImg}
                                                alt="Delete"
                                                style={{ marginLeft: "auto", cursor: "pointer" }}
                                                onClick={(e) => {
                                                    e.stopPropagation(); // 防止触发Radio选择
                                                    handleDelete(item);
                                                }}
                                            />
                                        </div>
                                    ))}
                                </Radio.Group>
                            ) : (
                                <div className="climate-shift-modal-content-enter-author-code-list">
                                    {searchQuery ? "No relevant favourites found" : "No Favourites Found"}
                                </div>
                            )}
                        </IF>
                    </Spin>

                    {/* 假如author有值，且ClimateShiftAddOrShowFavouritesEnum为Add，则直接输入Favourites Name */}
                    <IF show={!!authorCode && addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.ADD}>
                        <div className="climate-shift-modal-content-enter-author-code">
                            Enter Favourites Name
                        </div>
                        {/* For add Favourites */}
                        <Input
                            value={favouritesName}
                            onChange={(e) => {
                                publicHandleInput(e.target.value);
                            }}
                        />
                    </IF>

                    {/* 假如 author 没值 */}
                    <IF show={!authorCode}>
                        <Input
                            value={inputAuthorCode}
                            onChange={(e) => {
                                publicHandleAuthorCodeInput(e.target.value);
                            }}
                            placeholder="Define Username"
                        />
                        <div style={{ marginTop: "10px", marginBottom: "10px", color: "green" }}>
                            {
                                ifExistAuthorCode === undefined ? "" :
                                    (ifExistAuthorCode === true ? "Name match" : "Name new")
                            }
                        </div>
                    </IF>

                </div>


                {/* Button */}
                <div>
                    <div className="climate-shift-modal-content-button">
                        <IF show={!(!!authorCode && addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.SHOW)
                        }>
                            <div className="climate-shift-modal-content-button-item favourites-invoke"
                                onClick={() => {
                                    publicHandleProcess();
                                }}
                            >
                                Proceed
                            </div>
                        </IF>
                        <IF show={!!authorCode && (addOrShowFavouritesModal === ClimateShiftAddOrShowFavouritesEnum.SHOW)}>
                            <div className="climate-shift-modal-content-button-item favourites-invoke"
                                onClick={() => {
                                    handleOpen();
                                }}
                            >
                                Open
                            </div>
                        </IF>
                        <div className="climate-shift-modal-content-button-item favourites-cancel"
                            onClick={() => {
                                handleCancel();
                            }}
                        >
                            Cancel
                        </div>
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default FavouritesModal;